import React from 'react';
import { Box, Card, Typography, Stack } from '@mui/material';

import curveArrowRedUp from '../../assets/WidgetsIcon/curve-arrow-red-up.svg';
import pulseIcon from '../../assets/WidgetsIcon/pulse.svg';
import dollarIcon from '../../assets/WidgetsIcon/dollar.svg';
import curveArrowGreenDown from '../../assets/WidgetsIcon/curve-arrow-green-down.svg';
import { calculatePercentageChange } from '../../utils/helperFunctions';

function getCasualtyTotals(casualties, groupKey) {
  const group = casualties[groupKey] || {};
  const fatal = Number(group.fatal) || 0;
  const nonfatal = Number(group.nonfatal) || 0;
  return { fatal, nonfatal, total: fatal + nonfatal };
}

const IncidentImpact = ({ data, dateRange }) => {
  const civilianCasualties = getCasualtyTotals(data.casualties, 'nonff');
  const civilianCasualtiesPrev = getCasualtyTotals(
    data.casualties_old,
    'nonff'
  );
  const civilianChange = calculatePercentageChange(
    civilianCasualties.total,
    civilianCasualtiesPrev.total
  );

  const ffCasualties = getCasualtyTotals(data.casualties, 'ff');
  const ffCasualtiesPrev = getCasualtyTotals(data.casualties_old, 'ff');
  const ffChange = calculatePercentageChange(
    ffCasualties.total,
    ffCasualtiesPrev.total
  );
  const propertyDamage = data.property_damage;
  const propertyDamageOld = data.property_damage_old;

  const propertyDamageChange = calculatePercentageChange(
    propertyDamage,
    propertyDamageOld
  );

  return (
    <Card
      className="p-3"
      sx={{
        boxShadow: '0px 4px 4px 0px #0000000F',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            marginLeft: 1,
            fontWeight: 700,
            lineHeight: '20px',
          }}
        >
          Incident Impact
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Box
          className="d-flex flex-column justify-content-between"
          sx={{
            px: 2,
            py: '12px',
            border: '1px solid #E1E1E1',
            borderRadius: '8px',
            height: 170,
          }}
        >
          <Box className="d-flex">
            <img src={dollarIcon} alt="noticeIcon" />{' '}
            <Typography
              variant="h6"
              sx={{
                marginLeft: 1,
                fontWeight: 700,
                lineHeight: '20px',
              }}
            >
              Property Damage
            </Typography>
          </Box>
          <Box className="d-flex align-items-baseline mt-auto mb-2">
            <Typography
              variant="h4"
              sx={{
                marginRight: 1,
                fontWeight: 700,
              }}
            >
              ${propertyDamage}
            </Typography>
            <img
              src={
                propertyDamageChange <= 0
                  ? curveArrowGreenDown
                  : curveArrowRedUp
              }
              alt="curveArrow"
              className="me-1"
            />
            <Typography
              variant="body2"
              sx={{
                color: propertyDamageChange <= 0 ? '#45856B' : '#C71818',
              }}
              className="me-2"
            >
              {propertyDamageChange}%
            </Typography>
            <Typography variant="body2" sx={{ color: '#00000078' }}>
              previous {dateRange} days
            </Typography>
          </Box>
          <Typography variant="body2">total estimated damage</Typography>
        </Box>

        <Box
          className="d-flex flex-column justify-content-between"
          sx={{
            px: 2,
            py: '12px',
            border: '1px solid #E1E1E1',
            borderRadius: '8px',
            minHeight: 170,
            minWidth: 'fit-content',
          }}
        >
          <Box className="d-flex">
            <img src={pulseIcon} alt="noticeIcon" />{' '}
            <Typography
              variant="h6"
              sx={{
                marginLeft: 1,
                fontWeight: 700,
                lineHeight: '20px',
              }}
            >
              Casualties & Injuries
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: '48px',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '100%',
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                minWidth: '130px',
                pb: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flex: 1,
                height: '100%',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                  }}
                >
                  {civilianCasualties.total}{' '}
                </Typography>
                civilian
              </Typography>

              <Box className="d-flex">
                <img
                  src={
                    civilianChange <= 0 ? curveArrowGreenDown : curveArrowRedUp
                  }
                  alt="curveArrow"
                  className="me-1"
                  style={{ width: '16px' }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: civilianChange <= 0 ? '#45856B' : '#C71818',
                    fontWeight: 600,
                  }}
                  className="me-2"
                >
                  {civilianChange}%
                </Typography>
                <Typography variant="body2" sx={{ color: '#00000078' }}>
                  previous {dateRange} days
                </Typography>
              </Box>

              <Box className="d-flex">
                <Box sx={{ mr: 3 }}>
                  <Typography variant="body2" sx={{ lineHeight: '25px' }}>
                    {civilianCasualties.nonfatal} <br /> Injuries
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ lineHeight: '25px' }}>
                    {civilianCasualties.fatal} <br /> Fatalities
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: '130px',
                pb: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flex: 1,
                height: '100%',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: '20px',
                    fontWeight: 700,
                  }}
                >
                  {ffCasualties.total}{' '}
                </Typography>
                FF/EMS
              </Typography>

              <Box className="d-flex">
                <img
                  src={ffChange <= 0 ? curveArrowGreenDown : curveArrowRedUp}
                  alt="curveArrow"
                  className="me-1"
                  style={{ width: '16px' }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: ffChange <= 0 ? '#45856B' : '#C71818',
                    fontWeight: 600,
                  }}
                  className="me-2"
                >
                  {ffChange}%
                </Typography>
                <Typography variant="body2" sx={{ color: '#00000078' }}>
                  previous {dateRange} days
                </Typography>
              </Box>

              <Box className="d-flex">
                <Box sx={{ mr: 3 }}>
                  <Typography variant="body2" sx={{ lineHeight: '25px' }}>
                    {ffCasualties.nonfatal} <br /> Injuries
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ lineHeight: '25px' }}>
                    {ffCasualties.fatal} <br /> Fatalities
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Stack>
    </Card>
  );
};

export default IncidentImpact;
